import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { LogService, CommonLogMessages } from '../services/log.service';
import { HealthCheckService } from '../services/health-check.service';
import { environment } from '../../../environments/environment';
import { clearBrowserStorageKeepingPreferences } from '../utils/browser-storage.utils';
import { currentAppToken, isImpersonationActive } from '../auth/app-token';
import { AppTokenRenewalOutcome, AppTokenRenewalService } from '../auth/app-token-renewal.service';

/**
 * Status 0 (network error, CORS, gateway unreachable) and 5xx mean the backend is
 * unhealthy, not that the session is invalid — they must never log the user out.
 */
export function isBackendUnavailableError(err: HttpErrorResponse): boolean {
  return err.status === 0 || err.status >= 500;
}

/** Methods without side effects: safe to replay after a 0/5xx once the backend is healthy again. */
const REPLAYABLE_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/** 401 error codes that mean "not allowed here", not "session invalid" — no renewal, no logout. */
const AUTHORIZATION_ERROR_CODES = ['NOT_PROJECT_MEMBER', 'ADMIN_ACCESS_ONLY_ACCESS_DENIED'];

const transientAuthFailures = new WeakSet<object>();

/**
 * True for a 401 whose app-token renewal could not be attempted to completion (IAM unreachable,
 * 429, 5xx…). The session may still be valid, so callers must not log the user out for it.
 */
export function isTransientAuthFailure(err: unknown): boolean {
  return !!err && typeof err === 'object' && transientAuthFailures.has(err);
}

const permissionDenied401s = new WeakSet<object>();

/**
 * True for a 401 that came back again after IAM had just issued a fresh app token: the session is
 * valid, so the 401 is the backend refusing access (permission), not an expired session. Callers
 * must handle it like a 403 and never log the user out for it.
 */
export function isPermissionDenied401(err: unknown): boolean {
  return !!err && typeof err === 'object' && permissionDenied401s.has(err);
}

@Injectable({
  providedIn: 'root',
})
export class AppHttpInterceptor implements HttpInterceptor {
  constructor(
    private readonly logService: LogService,
    private readonly healthCheckService: HealthCheckService,
    private readonly appTokenRenewal: AppTokenRenewalService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // IAM SDK calls (login, SSO callback, auth-code exchange) and Drive service
    // calls must not carry the legacy app token, nor trigger the 401/503
    // logout-redirect below — they own their own auth (the Drive requests are
    // authenticated by mtmAppTokenInterceptor with the IAM app token). Let them
    // pass through untouched.
    const url = req.url;
    if (
      url.startsWith(environment.iam.iamApiUrl) ||
      url.startsWith(environment.drive.apiUrl) ||
      url.includes('/sso/callback') ||
      url.includes('/oauth2/authorization/') ||
      this.healthCheckService.isHealthCheckUrl(url)
    ) {
      return next.handle(req);
    }

    // You can clone request here to add headers, auth tokens, etc.
    const token = currentAppToken();
    let clonedReq = req;
    if (token && !req.headers.has('Authorization')) {
      clonedReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      });
    }

    return next.handle(clonedReq).pipe(
      catchError((err: HttpErrorResponse) => {
        if (isBackendUnavailableError(err)) {
          return this.retryIfBackendHealthy(clonedReq, next, err);
        }

        const sentWithAppToken = !!token && clonedReq.headers.get('Authorization') === `Bearer ${token}`;
        const canRenew = sentWithAppToken && !isImpersonationActive();

        if (err.status === 401) {
          this.logService.logMessage('warning', CommonLogMessages.Unauthorized, {
            err,
            url: req.url,
            cause: 'HttpInterceptor',
          });
          if (!canRenew || AUTHORIZATION_ERROR_CODES.includes(err.error?.errorCode)) {
            return throwError(() => err);
          }
          return this.renewAndReplay(req, next, err);
        }

        // For other errors: an expired token alone is not a reason to log out — only a renewal
        // that IAM rejects with 401 is.
        const jwtHelper = new JwtHelperService();
        if (canRenew && jwtHelper.isTokenExpired(token)) {
          return this.appTokenRenewal.renew().pipe(
            switchMap((outcome: AppTokenRenewalOutcome) => {
              if (outcome === 'session-invalid') {
                this.logOutExpiredSession(err, req.url);
              }
              return throwError(() => err);
            })
          );
        }

        return throwError(() => err);
      })
    );
  }

  /**
   * Gateway 401 on a request sent with the IAM app token: renew the token (one shared renewal for
   * concurrent 401s) and replay the request once. Only a renewal rejected by IAM with 401 lets the
   * 401 through to ApiService's logout; any other renewal failure keeps the session, and a replay
   * that still gets 401 with the fresh token is a permission refusal, not an expired session.
   */
  private renewAndReplay(
    req: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return this.appTokenRenewal.renew().pipe(
      switchMap((outcome: AppTokenRenewalOutcome) => {
        const freshToken = currentAppToken();
        if (outcome === 'renewed' && freshToken) {
          return next.handle(req.clone({ setHeaders: { Authorization: `Bearer ${freshToken}` } })).pipe(
            catchError((replayErr: HttpErrorResponse) => {
              if (replayErr.status === 401) {
                permissionDenied401s.add(replayErr);
              }
              return throwError(() => replayErr);
            })
          );
        }
        if (outcome === 'unavailable') {
          transientAuthFailures.add(err);
        }
        return throwError(() => err);
      })
    );
  }

  private logOutExpiredSession(err: HttpErrorResponse, url: string): void {
    this.logService.logMessage('warning', CommonLogMessages.TokenExpired, {
      err,
      url,
      cause: 'HttpInterceptor - others',
    });
    clearBrowserStorageKeepingPreferences();
    this.logService.setUser(null);
    window.location.href = '/entry';
  }

  /**
   * The request failed because the backend looks unavailable. Probe the health
   * endpoint (retried by HealthCheckService): if it answers, replay the request
   * once — only for safe methods, a POST/PUT/PATCH/DELETE may already have been
   * applied server-side; if it never answers, surface the original error and keep
   * the session.
   */
  private retryIfBackendHealthy(
    req: HttpRequest<any>,
    next: HttpHandler,
    err: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    if (!navigator.onLine) {
      return throwError(() => err);
    }

    return this.healthCheckService.probe().pipe(
      switchMap((healthy) => {
        if (healthy) {
          return REPLAYABLE_METHODS.includes(req.method) ? next.handle(req) : throwError(() => err);
        }

        const message = { action: 'backendStatus.down' };
        localStorage.setItem('message', JSON.stringify(message));
        localStorage.removeItem('message');
        return throwError(() => err);
      })
    );
  }
}
